import { useThree } from '@react-three/fiber';
import { useLayoutEffect, useRef } from 'react';
import { type DragConfig, useGesture } from '@use-gesture/react';
import { isVector3Like } from '@slope/three';
import * as THREE from 'three';

const initialModelPosition = /* @__PURE__ */ new THREE.Vector3();
const cursor = /* @__PURE__ */ new THREE.Vector2();
const target = /* @__PURE__ */ new THREE.Vector3();
const dragOffset = /* @__PURE__ */ new THREE.Vector3();
const dragPlaneNormal = /* @__PURE__ */ new THREE.Vector3();
const dragPlane = /* @__PURE__ */ new THREE.Plane();

type ControlsProto = {
  enabled: boolean;
};

type AnchorPreset = 'center' | 'bottom';

export type UseDragControlsOptions = {
  /** If autoTransform is true, automatically apply the local transform on drag, true */
  autoTransform?: boolean;
  /** The matrix to control */
  matrix?: THREE.Matrix4;
  /** Limits */
  dragLimits?: [
    [number, number] | undefined,
    [number, number] | undefined,
    [number, number] | undefined,
  ];
  /**
   * Function to project a pointer position into the 3D space.
   * If not provided, it will use the camera's world direction to create a drag plane.
   * The function should return a THREE.Vector3 or null if the projection fails.
   */
  project?: (
    pointer: THREE.Vector2,
    camera: THREE.Camera,
    target?: THREE.Vector3,
  ) => THREE.Vector3 | null;
  /** The anchor preset */
  anchor?: AnchorPreset;
  /** Hover event */
  onHover?: (hovering: boolean) => void;
  /** Drag start event */
  onDragStart?: (origin: THREE.Vector3) => void;
  /** Drag event */
  onDrag?: (
    localMatrix: THREE.Matrix4,
    deltaLocalMatrix: THREE.Matrix4,
    worldMatrix: THREE.Matrix4,
    deltaWorldMatrix: THREE.Matrix4,
  ) => void /** Drag end event */;
  onDragEnd?: () => void;
};

/**
 * Strongly inspired in https://github.com/pmndrs/drei/blob/master/src/web/DragControls.tsx
 *
 * This hook provides drag controls for a Three.js scene, allowing objects to be dragged with the mouse.
 *
 */
export const useDragControls = (
  {
    project,
    matrix,
    autoTransform = true,
    dragLimits,
    onHover,
    onDragStart,
    onDrag,
    onDragEnd,
    anchor,
  }: UseDragControlsOptions,
  config?: DragConfig,
) => {
  const ref = useRef<THREE.Group>(null);
  const controls = useThree(({ controls }) => controls as unknown as ControlsProto | undefined);
  const camera = useThree(({ camera }) => camera);
  const invalidate = useThree(({ invalidate }) => invalidate);
  const size = useThree(({ size }) => size);
  const raycaster = useThree(({ raycaster }) => raycaster as THREE.Raycaster);

  // Gesture bindings ---------------------------------------------------------------------------
  const bind = useGesture(
    {
      onHover: ({ hovering }) => onHover?.(hovering ?? false),
      onDragStart: ({ event }) => {
        if (!ref.current) {
          return;
        }
        if (controls) controls.enabled = false;
        const { point } = event as unknown as {
          point: THREE.Vector3;
        };

        ref.current.matrix.decompose(
          initialModelPosition,
          new THREE.Quaternion(),
          new THREE.Vector3(),
        );

        target.copy(point);
        if (offsetRef.current) {
          dragOffset.copy(offsetRef.current);
        } else {
          dragOffset.copy(target).sub(initialModelPosition);
        }

        onDragStart?.(initialModelPosition);
        invalidate();
      },
      onDrag: ({ xy: [dragX, dragY], intentional }) => {
        if (controls) controls.enabled = false;
        if (!ref.current) {
          return;
        }
        if (!intentional) return;
        const normalizedMouseX = ((dragX - size.left) / size.width) * 2 - 1;
        const normalizedMouseY = -((dragY - size.top) / size.height) * 2 + 1;

        cursor.set(normalizedMouseX, normalizedMouseY);

        if (project) {
          project(cursor, camera, target);
        } else {
          camera.getWorldDirection(dragPlaneNormal).negate();
          dragPlane.setFromNormalAndCoplanarPoint(dragPlaneNormal, target);
          raycaster.setFromCamera(cursor, camera);
          raycaster.ray.intersectPlane(dragPlane, target);
        }

        const previousLocalMatrix = ref.current.matrix.clone();
        const previousWorldMatrix = ref.current.matrixWorld.clone();

        const intendedNewPosition = new THREE.Vector3(
          target.x - dragOffset.x,
          target.y - dragOffset.y,
          target.z - dragOffset.z,
        );

        if (dragLimits) {
          intendedNewPosition.x = dragLimits[0]
            ? Math.max(Math.min(intendedNewPosition.x, dragLimits[0][1]), dragLimits[0][0])
            : intendedNewPosition.x;
          intendedNewPosition.y = dragLimits[1]
            ? Math.max(Math.min(intendedNewPosition.y, dragLimits[1][1]), dragLimits[1][0])
            : intendedNewPosition.y;
          intendedNewPosition.z = dragLimits[2]
            ? Math.max(Math.min(intendedNewPosition.z, dragLimits[2][1]), dragLimits[2][0])
            : intendedNewPosition.z;
        }

        if (autoTransform) {
          ref.current.matrix.setPosition(intendedNewPosition);

          const deltaLocalMatrix = ref.current.matrix
            .clone()
            .multiply(previousLocalMatrix.invert());
          const deltaWorldMatrix = ref.current.matrix
            .clone()
            .multiply(previousWorldMatrix.invert());

          onDrag?.(ref.current.matrix, deltaLocalMatrix, ref.current.matrixWorld, deltaWorldMatrix);
        } else {
          const tempMatrix = new THREE.Matrix4().copy(ref.current.matrix);
          tempMatrix.setPosition(intendedNewPosition);

          const deltaLocalMatrix = tempMatrix.clone().multiply(previousLocalMatrix.invert());
          const deltaWorldMatrix = tempMatrix.clone().multiply(previousWorldMatrix.invert());

          onDrag?.(tempMatrix, deltaLocalMatrix, ref.current.matrixWorld, deltaWorldMatrix);
        }
        invalidate();
      },
      onDragEnd: () => {
        if (controls) controls.enabled = true;

        onDragEnd?.();
        invalidate();
      },
    },
    {
      drag: {
        filterTaps: true,
        threshold: 1,
        ...config,
      },
    },
  );

  useLayoutEffect(() => {
    if (!matrix || !ref.current) return;

    // If the matrix is a real matrix4 it means that the user wants to control the gizmo
    // In that case it should just be set, as a bare prop update would merely copy it
    ref.current.matrix = matrix;
  }, [matrix]);

  const offsetRef = useRef<THREE.Vector3>(new THREE.Vector3());

  useLayoutEffect(() => {
    if (!ref.current) return;
    if (isVector3Like(anchor)) {
      offsetRef.current.copy(anchor);
      return;
    }
    if (anchor === 'center') {
      offsetRef.current.set(0, 0, 0);
      return;
    }
    const box = new THREE.Box3().setFromObject(ref.current);
    const size = new THREE.Vector3();
    box.getSize(size);
    offsetRef.current.set(0, -size.y / 2, 0);
  }, [anchor]);

  return { ref, matrix, matrixAutoUpdate: false, ...bind() } as const;
};
